import { Check, X, Plus, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface DismissibleChipProps {
  label: string;
  /** Show X icon to remove this chip */
  onRemove?: () => void;
  /** Show + icon to add this chip (search result style) */
  onAdd?: () => void;
  /** Show check icon to confirm/promote this chip (renders before onRemove) */
  onConfirm?: () => void;
  /**
   * Show a helper info icon instead of a remove control. Used for
   * system-derived chips that cannot be removed directly.
   */
  onInfo?: () => void;
  className?: string;
  /** Custom class for the remove (X) button */
  removeClassName?: string;
  /** Custom class for the add (+) button */
  addClassName?: string;
  /** Custom class for the confirm/check button */
  confirmClassName?: string;
  /** Custom class for the info button */
  infoClassName?: string;
  /** Optional status indicator, e.g. provisional disease history */
  status?: "provisional" | "confirmed";
}

/**
 * Shared chip/badge used across patient cards, dialogs, and recipe tag rows.
 * Pass only the action controls needed by the caller.
 */
export const DismissibleChip = ({
  label,
  onRemove,
  onAdd,
  onConfirm,
  onInfo,
  className,
  removeClassName,
  addClassName,
  confirmClassName,
  infoClassName,
  status,
}: DismissibleChipProps) => (
  <Badge
    variant="secondary"
    className={cn(
      "h-7 px-3 z-caption font-normal gap-2 transition-colors rounded-full border border-border/80 bg-card text-foreground",
      status === "provisional" &&
        "border-warning/30 bg-warning/10 text-warning",
      onAdd &&
        "cursor-pointer hover:bg-muted hover:border-muted-foreground/30 group",
      className
    )}
    onClick={
      onAdd
        ? (e) => {
            e.stopPropagation();
            onAdd();
          }
        : undefined
    }
  >
    {status === "provisional" && (
      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-warning/20 text-warning dark:text-warning z-hint font-bold mr-0.5">
        ?
      </span>
    )}
    {label}
    {onConfirm && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onConfirm();
        }}
        className={cn(
          "rounded p-0.5 transition-colors bg-background/80 text-muted-foreground hover:bg-success hover:text-success",
          confirmClassName
        )}
        title="Confirm"
      >
        <Check className="h-3 w-3" />
      </button>
    )}
    {onRemove && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={cn(
          "rounded p-0.5 transition-colors bg-background/80 text-muted-foreground hover:bg-destructive/20 hover:text-destructive",
          removeClassName
        )}
      >
        <X className="h-3 w-3" />
      </button>
    )}
    {onAdd && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        className={cn(
          "rounded p-0.5 transition-colors bg-background/80 text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary",
          addClassName
        )}
      >
        <Plus className="h-3 w-3" />
      </button>
    )}
    {onInfo && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onInfo();
        }}
        className={cn(
          "rounded-full p-0.5 transition-colors text-muted-foreground/70 hover:bg-foreground/10 hover:text-foreground",
          infoClassName
        )}
        aria-label="Why can't this be removed?"
      >
        <Info className="h-3 w-3" />
      </button>
    )}
  </Badge>
);

/**
 * Container for DismissibleChip items.
 * Provides a muted background, rounded corners, and themed scrollbar.
 */
export const ChipContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex flex-wrap gap-1.5 max-h-24 overflow-y-auto scrollbar-thin",
      className
    )}
  >
    {children}
  </div>
);
